// AddEditCourseExamSubPattern.jsx
// ✅ FIXED:
// 1) Per-pattern evaluated_by respected (MARKS/GRADE) -> Grade pattern now shows Grade dropdown
// 2) No "first pattern mode" override
// 3) No "only one pattern id" filter (so same course ke multiple patterns show honge)
// 4) Auto-fill from Pattern Master: (MARKS => total/passing), (GRADE => passing_grade)

import React, { useEffect, useRef, useState, useMemo } from 'react';
import {
  Card,
  CardTitle,
  CardBody,
  Button,
  Form,
  FormGroup,
  Label,
  FormText,
} from 'reactstrap';
import { useNavigate, useLocation } from 'react-router-dom';
import VendorBranchSelector from '../../components/VendorBranchSelector';
import { useSelector } from 'react-redux';
import { apiService } from '../../constants/ApiService';
import { toast } from 'react-toastify';
import useVendorBranch from '../../utils/useVendorBranch';
import Select from 'react-select';
import conditionalDataLoad from '../../utils/conditionalDataLoad';

const EXAM_PATTERN_MASTER_ROUTE = '/save-course-exam-pattern';
const EVALUATED_BY = { MARKS: 'MARKS', GRADE: 'GRADE' };

export const GRADE_SCALE = ['A+', 'A', 'B+', 'B', 'C+', 'C', 'D', 'E', 'F'].map(
  (g) => ({ value: g, label: g }),
);

const EVAL_OPTIONS = [
  { value: EVALUATED_BY.MARKS, label: 'Marks' },
  { value: EVALUATED_BY.GRADE, label: 'Grade' },
];

const makeTempId = () =>
  (crypto?.randomUUID && crypto.randomUUID()) ||
  Math.random().toString(36).slice(2);

const isEmpty = (v) => String(v ?? '').trim() === '';
const toNum = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

const normalizeEval = (v) => {
  const s = String(v || '')
    .trim()
    .toUpperCase();
  return s === EVALUATED_BY.GRADE ? EVALUATED_BY.GRADE : EVALUATED_BY.MARKS;
};

// -----------------------------
// Course Structure helpers
// -----------------------------
const normalizeCourseStructure = (raw) => {
  const s = String(raw ?? '')
    .trim()
    .toLowerCase();
  if (!s) return 'semester';
  if (s.includes('year')) return 'year';
  if (s.includes('month')) return 'month';
  if (s.includes('sem')) return 'semester';
  return 'semester';
};

const structureWordUpper = (raw) => {
  const s = normalizeCourseStructure(raw);
  if (s === 'year') return 'YEAR';
  if (s === 'month') return 'MONTH';
  return 'SEMESTER';
};

const structureHeader = (raw, index1Based) =>
  `${structureWordUpper(raw)} - ${index1Based}`;

// -----------------------------
// helpers (API response shape safe)
// -----------------------------
const getCaseInsensitive = (obj, key) => {
  if (!obj || typeof obj !== 'object') return undefined;
  const foundKey = Object.keys(obj).find(
    (k) => k.toLowerCase() === String(key).toLowerCase(),
  );
  return foundKey ? obj[foundKey] : undefined;
};

const normalizeArray = (payload) => {
  if (!payload) return [];
  if (Array.isArray(payload)) return payload;

  const directKeys = [
    'Data',
    'data',
    'ClassSubjectList',
    'classSubjectList',
    'List',
    'list',
    'Result',
    'result',
    'Items',
    'items',
  ];

  for (const k of directKeys) {
    const v = getCaseInsensitive(payload, k);
    if (Array.isArray(v)) return v;
  }

  const nested1 = getCaseInsensitive(payload, 'data');
  const nested2 = getCaseInsensitive(payload, 'Data');

  if (nested1 && typeof nested1 === 'object') {
    const a = normalizeArray(nested1);
    if (a.length) return a;
  }
  if (nested2 && typeof nested2 === 'object') {
    const a = normalizeArray(nested2);
    if (a.length) return a;
  }

  return [];
};

const flattenIfGrouped = (arr) => {
  if (!Array.isArray(arr) || arr.length === 0) return [];

  const hasInnerList = arr.some((x) => {
    const a1 = getCaseInsensitive(x, 'SubjectList');
    const a2 = getCaseInsensitive(x, 'subjectList');
    const a3 = getCaseInsensitive(x, 'Subjects');
    const a4 = getCaseInsensitive(x, 'subjects');
    return (
      Array.isArray(a1) ||
      Array.isArray(a2) ||
      Array.isArray(a3) ||
      Array.isArray(a4)
    );
  });

  if (!hasInnerList) return arr;

  const flat = [];
  arr.forEach((x) => {
    const a1 = getCaseInsensitive(x, 'SubjectList');
    const a2 = getCaseInsensitive(x, 'subjectList');
    const a3 = getCaseInsensitive(x, 'Subjects');
    const a4 = getCaseInsensitive(x, 'subjects');

    const inner =
      (Array.isArray(a1) && a1) ||
      (Array.isArray(a2) && a2) ||
      (Array.isArray(a3) && a3) ||
      (Array.isArray(a4) && a4) ||
      [];

    inner.forEach((y) => flat.push(y));
  });

  return flat;
};

// -----------------------------
// Pattern pickers
// -----------------------------
const pickPatternId = (p) =>
  Number(
    p?.id ??
      p?.ID ??
      getCaseInsensitive(p, 'id') ??
      getCaseInsensitive(p, 'ID') ??
      0,
  );

const pickTermName = (p) =>
  p?.term_name ??
  p?.termName ??
  p?.Term_Name ??
  getCaseInsensitive(p, 'term_name') ??
  getCaseInsensitive(p, 'termName') ??
  getCaseInsensitive(p, 'Term_Name') ??
  '';

const pickTotalMarks = (p) =>
  p?.total_marks ??
  p?.totalMarks ??
  getCaseInsensitive(p, 'total_marks') ??
  getCaseInsensitive(p, 'totalMarks') ??
  '';

const pickPassingMarks = (p) =>
  p?.passing_marks ??
  p?.passingMarks ??
  getCaseInsensitive(p, 'passing_marks') ??
  getCaseInsensitive(p, 'passingMarks') ??
  '';

const pickPassingGrade = (p) =>
  p?.passing_grade ??
  p?.passingGrade ??
  getCaseInsensitive(p, 'passing_grade') ??
  getCaseInsensitive(p, 'passingGrade') ??
  '';

const pickWeightage = (p) =>
  p?.weightage_percet ??
  p?.weightagePercent ??
  getCaseInsensitive(p, 'weightage_percet') ??
  getCaseInsensitive(p, 'weightagePercent') ??
  '';

const pickPatternSemesterNo = (p) => {
  const v =
    p?.semester_no ??
    p?.Semester_No ??
    p?.semester ??
    p?.Semester ??
    p?.sem_no ??
    p?.Sem_No ??
    getCaseInsensitive(p, 'semester_no') ??
    getCaseInsensitive(p, 'semester') ??
    getCaseInsensitive(p, 'sem_no') ??
    0;
  return Number(v || 0);
};

const normalizeTermType = (raw) => {
  const s = String(raw || '')
    .trim()
    .toUpperCase();
  if (!s) return '';
  if (s.includes('MID')) return 'MIDTERM';
  if (s.includes('INTERNAL')) return 'INTERNAL';
  if (s.includes('UNIT')) return 'UNIT';
  if (s.includes('FINAL')) return 'FINAL';
  if (s.includes('SEM')) return 'SEMESTER';
  if (s.includes('END')) return 'SEMESTER';
  return s;
};

const pickTermType = (p) => {
  const direct =
    p?.term_type ??
    p?.termType ??
    p?.exam_term ??
    p?.examTerm ??
    getCaseInsensitive(p, 'term_type') ??
    getCaseInsensitive(p, 'termType') ??
    getCaseInsensitive(p, 'exam_term') ??
    getCaseInsensitive(p, 'examTerm') ??
    '';
  const inferred = direct || pickTermName(p) || '';
  return normalizeTermType(inferred);
};

const pickEvaluatedBy = (p) => {
  const v =
    p?.evaluated_by ??
    p?.evaluatedBy ??
    getCaseInsensitive(p, 'evaluated_by') ??
    getCaseInsensitive(p, 'evaluatedBy') ??
    '';
  return normalizeEval(v);
};

// -----------------------------
// Subject pickers
// -----------------------------
const pickSubjectId = (s) => {
  const direct =
    s?.subject_id ??
    s?.SubjectId ??
    s?.Subject_ID ??
    s?.subjectId ??
    s?.SubjectID ??
    s?.subject_master_id ??
    s?.subjectMasterId ??
    s?.SubjectMasterId;

  const nested =
    s?.subject?.id ??
    s?.subject?.subject_id ??
    s?.Subject?.id ??
    s?.Subject?.subject_id;

  return Number(direct ?? nested ?? 0);
};

const pickSubjectName = (s) => {
  const direct =
    s?.subject_name ?? s?.Subject_Name ?? s?.name ?? s?.Name ?? s?.subjectName;

  const nested =
    s?.subject?.subject_name ??
    s?.subject?.name ??
    s?.Subject?.subject_name ??
    s?.Subject?.name;

  return String(direct ?? nested ?? '');
};

const pickSubjectCode = (s) => {
  const direct =
    s?.subject_code ?? s?.Subject_Code ?? s?.code ?? s?.subjectCode;

  const nested =
    s?.subject?.subject_code ??
    s?.subject?.code ??
    s?.Subject?.subject_code ??
    s?.Subject?.code;

  return String(direct ?? nested ?? '');
};

const pickSubjectSemesterNo = (s) => {
  const v =
    s?.semester ??
    s?.Semester ??
    s?.semester_no ??
    s?.Semester_No ??
    s?.semesterNo ??
    getCaseInsensitive(s, 'semester') ??
    getCaseInsensitive(s, 'semester_no') ??
    getCaseInsensitive(s, 'semesterNo') ??
    1;

  return Number(v || 1);
};

// -----------------------------
// Component
// -----------------------------
const AddEditCourseExamSubPattern = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useSelector((state) => state.authState);

  const { isSuperAdmin, isAdmin, hasMultiBranch, hasSingleBranch } =
    useVendorBranch(user);

  const [errorMessage, setErrorMessage] = useState({});
  const [selectionData, setSelectionData] = useState({
    vendor_id: 0,
    branch_id: 0,
  });

  const [rawCourses, setRawCourses] = useState([]);
  const [courseOptions, setCourseOptions] = useState([]);

  const [courseData, setCourseData] = useState({
    course_id: 0,
    course_name: '',
  });

  const [totalSemester, setTotalSemester] = useState(0);
  const [courseStructure, setCourseStructure] = useState('semester');

  const [patternList, setPatternList] = useState([]);
  const [termSubjects, setTermSubjects] = useState({});
  const [isEditMode, setIsEditMode] = useState(false);

  // global fallback only (per-pattern mode handled)
  const [evaluatedByMode, setEvaluatedByMode] = useState(EVALUATED_BY.MARKS);

  const reqRef = useRef(0);
  const bootRef = useRef(false);

  // If you navigate from Pattern List, pass: { fromPatternList: true, vendor_id, branch_id, course_id, course_name, total_semester, course_structure }
  const fromPatternList = Boolean(location?.state?.fromPatternList);
  const fixedCourseFromState = Number(location?.state?.course_id || 0) > 0;

  // ----------------------------
  // Load Courses
  // ----------------------------
  const fetchCourses = async (searchText = '') => {
    try {
      const vendorId = Number(selectionData.vendor_id || 0);
      const branchId = Number(selectionData.branch_id || 0);
      // if (!vendorId || !branchId) return;

      const res = await apiService.get(
        `api/ClassMaster/GetClassMaster?vendorId=${vendorId}&branchId=${branchId}&searchItem=${encodeURIComponent(
          searchText || '',
        )}`,
      );

      if (res?.status === true) {
        const list = normalizeArray(res);
        setRawCourses(list || []);
        setCourseOptions(
          (list || []).map((x) => ({
            value: x.id,
            label:
              x.class_Name ?? x.class_display_name ?? x.className ?? 'No Name',
          })),
        );
      } else {
        toast.error(res?.message || res?.Message || 'Failed to load courses');
      }
    } catch (err) {
      console.error(err);
      toast.error('Failed to load courses');
    }
  };

  const fetchCourseStructureFromServer = async (
    vendorId,
    branchId,
    courseId,
  ) => {
    const v = Number(vendorId || 0);
    const b = Number(branchId || 0);
    const cid = Number(courseId || 0);
    if (!v || !b || !cid) return;

    const existing = (rawCourses || []).find((x) => Number(x.id) === cid);
    if (existing) {
      const cs =
        existing?.course_structure ??
        existing?.courseStructure ??
        getCaseInsensitive(existing, 'course_structure') ??
        getCaseInsensitive(existing, 'courseStructure') ??
        'semester';
      setCourseStructure(normalizeCourseStructure(cs));
      return;
    }

    const res = await apiService.get(
      `api/ClassMaster/GetClassMaster?vendorId=${v}&branchId=${b}&searchItem=`,
    );
    if (res?.status !== true) return;

    const list = normalizeArray(res);
    if (!Array.isArray(list)) return;

    setRawCourses(list);
    setCourseOptions(
      list.map((x) => ({
        value: x.id,
        label: x.class_Name ?? x.class_display_name ?? x.className ?? 'No Name',
      })),
    );

    const cls = list.find((x) => Number(x.id) === cid);
    if (cls) {
      const cs =
        cls?.course_structure ??
        cls?.courseStructure ??
        getCaseInsensitive(cls, 'course_structure') ??
        getCaseInsensitive(cls, 'courseStructure') ??
        'semester';
      setCourseStructure(normalizeCourseStructure(cs));
    }
  };

  // ----------------------------
  // Pattern Master (✅ FIXED: no first-row evaluated_by override)
  // ----------------------------
  const loadCourseExamPatternMaster = async (vendorId, branchId, courseId) => {
    try {
      const v = vendorId ? Number(vendorId) : '';
      const b = branchId ? Number(branchId) : '';

      const url = `api/ExamMaster/GetAllCourseExamPattern?vendorId=${v}&branchId=${b}&courseId=${courseId}&search=`;
      const res = await apiService.get(url);

      if (res?.status === true) {
        const list = normalizeArray(res);
        setPatternList(list || []);

        // global fallback only
        setEvaluatedByMode(EVALUATED_BY.MARKS);

        return { list: list || [] };
      }

      setPatternList([]);
      setEvaluatedByMode(EVALUATED_BY.MARKS);
      return { list: [] };
    } catch (err) {
      console.error(err);
      setPatternList([]);
      setEvaluatedByMode(EVALUATED_BY.MARKS);
      return { list: [] };
    }
  };

  // ----------------------------
  // Subjects
  // ----------------------------
  const loadClassSubjects = async (vendorId, branchId, courseId) => {
    try {
      if (!courseId) return [];

      const params = new URLSearchParams();
      if (vendorId) params.append('vendorId', vendorId);
      if (branchId) params.append('branchId', branchId);
      params.append('classId', courseId);

      const url = `api/ClassMaster/GetClassSubjects?${params.toString()}`;
      const res = await apiService.get(url);

      let list = normalizeArray(res);
      list = flattenIfGrouped(list);

      return list || [];
    } catch (err) {
      console.error(err);
      toast.error('Error loading course subjects');
      return [];
    }
  };

  // ----------------------------
  // Existing mapping
  // ----------------------------
  const loadExistingSubPattern = async (vendorId, branchId, courseId) => {
    try {
      const v = vendorId ? Number(vendorId) : '';
      const b = branchId ? Number(branchId) : '';

      const res = await apiService.get(
        `api/ExamMaster/GetAllCourseExamSubPattern?vendorId=${v}&branchId=${b}&courseId=${courseId}&patternId=&subjectId=&search=`,
      );

      if (res?.status !== true) return [];
      return normalizeArray(res);
    } catch (err) {
      console.error(err);
      return [];
    }
  };

  // ----------------------------
  // Build rows (✅ FIXED: per-pattern evaluated_by + grade shows grade)
  // ----------------------------
  const buildTermSubjects = (patterns, subjectsAll, existingRows, courseId) => {
    const exMap = {};
    (existingRows || []).forEach((x) => {
      const pid = Number(x.pattern_id || 0);
      const sid = Number(x.subject_id || 0);
      if (!pid || !sid) return;

      exMap[`${pid}|${sid}`] = {
        id: Number(x.id || 0),
        max_marks: x.max_marks ?? '',
        passing_marks: x.passing_marks ?? '',
        passing_grade: x.passing_grade ?? x.passingGrade ?? '',
        evaluated_by: normalizeEval(x.evaluated_by ?? x.evaluatedBy ?? ''),
        sem_no:
          Number(x.sem_no ?? x.semester_no ?? x.semNo ?? 0) ||
          Number(getCaseInsensitive(x, 'sem_no') ?? 0) ||
          0,
      };
    });

    const anyExisting = (existingRows || []).some(
      (x) => Number(x?.id || 0) > 0,
    );
    setIsEditMode(anyExisting);

    const subjectsBySem = {};
    (subjectsAll || []).forEach((s) => {
      const sem = pickSubjectSemesterNo(s) || 1;
      if (!subjectsBySem[sem]) subjectsBySem[sem] = [];
      subjectsBySem[sem].push(s);
    });

    const next = {};
    (patterns || []).forEach((p) => {
      const pid = pickPatternId(p);
      if (!pid) return;

      const pSem = pickPatternSemesterNo(p);
      const usedSubjects =
        pSem && subjectsBySem[pSem] ? subjectsBySem[pSem] : subjectsAll;

      // ✅ IMPORTANT: per-pattern mode
      const defaultMode = normalizeEval(pickEvaluatedBy(p) || evaluatedByMode);

      // ✅ Pattern defaults (auto-fill for new rows)
      const pTotal = pickTotalMarks(p) ?? '';
      const pPass = pickPassingMarks(p) ?? '';
      const pGrade = pickPassingGrade(p) ?? '';

      next[pid] = (usedSubjects || [])
        .map((s) => {
          const sid = pickSubjectId(s);
          if (!sid) return null;

          const ex = exMap[`${pid}|${sid}`];
          const rowMode = normalizeEval(ex?.evaluated_by || defaultMode);

          const isExistingRow = ex?.id && Number(ex.id) > 0;

          const exMax = ex?.max_marks ?? '';
          const exPass = ex?.passing_marks ?? '';
          const exGrade = ex?.passing_grade ?? '';

          const semFromSubject = pickSubjectSemesterNo(s) || 1;
          const semFinal = Number(ex?.sem_no || semFromSubject || 1);

          // ✅ Only for NEW row => use pattern defaults
          const dMax = !isExistingRow ? pTotal : '';
          const dPass = !isExistingRow ? pPass : '';
          const dGrade = !isExistingRow ? pGrade : '';

          return {
            tempId: makeTempId(),
            id: ex?.id || 0,
            course_id: Number(courseId),
            pattern_id: pid,
            subject_id: sid,

            semester_no: semFinal,
            sem_no: semFinal,

            subject_name: pickSubjectName(s) || '',
            subject_code: pickSubjectCode(s) || '',

            evaluated_by: rowMode,

            // ✅ Correct UI values by mode
            max_marks:
              rowMode === EVALUATED_BY.MARKS
                ? exMax !== ''
                  ? exMax
                  : dMax
                : '',
            passing_marks:
              rowMode === EVALUATED_BY.MARKS
                ? exPass !== ''
                  ? exPass
                  : dPass
                : '',
            passing_grade:
              rowMode === EVALUATED_BY.GRADE
                ? exGrade !== ''
                  ? exGrade
                  : dGrade
                : '',
          };
        })
        .filter(Boolean);
    });

    setTermSubjects(next);
  };

  // ----------------------------
  // Load All
  // ----------------------------
  const loadAllForCourse = async (
    vendorId,
    branchId,
    courseId,
    courseName,
    ts,
    csFromNav,
  ) => {
    if (!courseId) return;

    const reqId = ++reqRef.current;

    setCourseData({
      course_id: Number(courseId),
      course_name: courseName || '',
    });

    if (Number(ts || 0) > 0) setTotalSemester(Number(ts || 0));

    if (csFromNav) setCourseStructure(normalizeCourseStructure(csFromNav));
    else await fetchCourseStructureFromServer(vendorId, branchId, courseId);

    const { list: patterns } = await loadCourseExamPatternMaster(
      vendorId || '',
      branchId || '',
      courseId,
    );
    if (reqRef.current !== reqId) return;

    if (!patterns || patterns.length === 0) {
      setTermSubjects({});
      toast.error(
        'Exam Pattern is not created for this course. Please add Exam Pattern first.',
      );
      navigate(EXAM_PATTERN_MASTER_ROUTE, {
        state: {
          vendor_id: vendorId || '',
          branch_id: branchId || '',
          course_id: Number(courseId),
          course_name: courseName || '',
          total_semester: Number(ts || 0),
          course_structure: csFromNav || '',
          mode: 'add',
          fromCourseList: true,
        },
      });
      return;
    }

    const subjects = await loadClassSubjects(
      vendorId || '',
      branchId || '',
      courseId,
    );
    if (reqRef.current !== reqId) return;

    const filteredSubjects = (subjects || []).filter(
      (x) => Number(pickSubjectId(x)) > 0,
    );
    if (!filteredSubjects || filteredSubjects.length === 0) {
      setTermSubjects({});
      toast.error('No subjects found for this course');
      return;
    }

    if (!Number(ts || 0) || Number(ts || 0) <= 0) {
      const maxSem = Math.max(
        0,
        ...(filteredSubjects || []).map((s) => pickSubjectSemesterNo(s) || 1),
      );
      setTotalSemester(maxSem || 0);
    }

    const existing = await loadExistingSubPattern(
      vendorId || '',
      branchId || '',
      courseId,
    );
    if (reqRef.current !== reqId) return;

    // ✅ IMPORTANT: NO filter — show ALL patterns (MARKS + GRADE)
    buildTermSubjects(patterns, filteredSubjects, existing, courseId);
  };

  // ----------------------------
  // Boot from navigation
  // ----------------------------
  useEffect(() => {
    if (bootRef.current) return;
    bootRef.current = true;

    const st = location?.state || {};
    const v = Number(st.vendor_id || 0);
    const b = Number(st.branch_id || 0);
    const cid = Number(st.course_id || 0);
    const cname = st.course_name || st.class_Name || st.courseName || '';
    const ts = Number(st.total_semester || st.Total_Semester || 0);

    const csFromNav =
      st.course_structure || st.courseStructure || st.Course_Structure || '';

    if (v > 0 || b > 0) setSelectionData({ vendor_id: v, branch_id: b });

    if (cid > 0) {
      setCourseOptions((prev) => {
        const exists = (prev || []).some((x) => Number(x.value) === cid);
        if (exists) return prev;
        return [
          ...(prev || []),
          { value: cid, label: cname || `Course-${cid}` },
        ];
      });

      setTimeout(() => {
        loadAllForCourse(v || '', b || '', cid, cname, ts, csFromNav);
      }, 0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state]);

  // ----------------------------
  // vendor/branch change -> load course list
  // ----------------------------
  useEffect(() => {
    if (fromPatternList || fixedCourseFromState) return;

    const canLoad = conditionalDataLoad({
      hasMultiBranch,
      hasSingleBranch,
      isSuperAdmin,
      isAdmin,
      vendorId: selectionData.vendor_id,
      branchId: selectionData.branch_id,
    });

    if (canLoad) fetchCourses('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectionData.vendor_id, selectionData.branch_id]);

  // ----------------------------
  // Course dropdown change
  // ----------------------------
  const onCourseSelectChange = async (opt) => {
    if (!opt) {
      setCourseData({ course_id: 0, course_name: '' });
      setTotalSemester(0);
      setPatternList([]);
      setTermSubjects({});
      setIsEditMode(false);
      setEvaluatedByMode(EVALUATED_BY.MARKS);
      setCourseStructure('semester');
      return;
    }

    const courseId = Number(opt.value || 0);
    const cls = (rawCourses || []).find(
      (x) => Number(x.id) === Number(courseId),
    );
    const ts = Number(cls?.total_semester || cls?.Total_Semester || 0);

    const cs =
      cls?.course_structure ??
      cls?.courseStructure ??
      getCaseInsensitive(cls, 'course_structure') ??
      getCaseInsensitive(cls, 'courseStructure') ??
      '';

    if (cs) setCourseStructure(normalizeCourseStructure(cs));
    else
      await fetchCourseStructureFromServer(
        selectionData.vendor_id,
        selectionData.branch_id,
        courseId,
      );

    await loadAllForCourse(
      selectionData.vendor_id || '',
      selectionData.branch_id || '',
      courseId,
      opt.label || '',
      ts,
      cs,
    );
  };

  // ----------------------------
  // update row
  // ----------------------------
  const updateRowField = (patternId, index, name, value) => {
    setTermSubjects((prev) => {
      const list = [...(prev[patternId] || [])];
      const row = { ...list[index] };
      row[name] = value;
      if (name === 'semester_no') row.sem_no = Number(value || 1);
      list[index] = row;
      return { ...prev, [patternId]: list };
    });
  };

  // ✅ Row EvaluatedBy switch (clear opposite values)
  const updateRowEvaluatedBy = (patternId, index, mode) => {
    const val = normalizeEval(mode);

    setTermSubjects((prev) => {
      const list = [...(prev[patternId] || [])];
      const row = { ...list[index] };

      row.evaluated_by = val;

      if (val === EVALUATED_BY.GRADE) {
        row.max_marks = '';
        row.passing_marks = '';
      } else {
        row.passing_grade = '';
        row.max_marks = '';
        row.passing_marks = '';
      }

      list[index] = row;
      return { ...prev, [patternId]: list };
    });
  };

  // ----------------------------
  // term types + map
  // ----------------------------
  const termTypes = useMemo(() => {
    const set = new Set();
    (patternList || []).forEach((p) => {
      const tt = pickTermType(p);
      if (tt) set.add(tt);
    });

    if (set.size === 0) return [];

    const ordered = [];
    ['MIDTERM', 'INTERNAL', 'UNIT', 'SEMESTER', 'FINAL'].forEach((k) => {
      if (set.has(k)) ordered.push(k);
      set.delete(k);
    });
    Array.from(set).forEach((k) => ordered.push(k));
    return ordered;
  }, [patternList]);

  const patternBySemTerm = useMemo(() => {
    const map = {};
    (patternList || []).forEach((p) => {
      const pid = pickPatternId(p);
      if (!pid) return;
      const sem = pickPatternSemesterNo(p) || 0;
      const tt = pickTermType(p);
      map[`${sem}|${tt || pid}`] = p;
    });
    return map;
  }, [patternList]);

  // ----------------------------
  // Save
  // ----------------------------
  const Save = async () => {
    if (!courseData.course_id || Number(courseData.course_id) <= 0) {
      toast.error('Please select Course');
      return;
    }

    const insertList = [];
    const updateList = [];
    let hasAnyValid = false;
    let hasInvalid = false;

    Object.keys(termSubjects || {}).forEach((pidStr) => {
      const pid = Number(pidStr);
      const rows = termSubjects[pid] || [];

      rows.forEach((r) => {
        const mode = normalizeEval(r.evaluated_by || evaluatedByMode);
        const isExisting = r.id && Number(r.id) > 0;

        const semNo = Number(r.sem_no || r.semester_no || 1);

        if (mode === EVALUATED_BY.GRADE) {
          const g = String(r.passing_grade ?? '').trim();
          if (isEmpty(g)) return;

          hasAnyValid = true;

          const obj = {
            id: isExisting ? Number(r.id) : null,
            vendor_id: selectionData.vendor_id
              ? Number(selectionData.vendor_id)
              : null,
            branch_id: selectionData.branch_id
              ? Number(selectionData.branch_id)
              : null,
            course_id: Number(courseData.course_id),
            pattern_id: pid,
            subject_id: Number(r.subject_id),
            sem_no: semNo,
            evaluated_by: EVALUATED_BY.GRADE,
            passing_grade: g,
            max_marks: null,
            passing_marks: null,
          };

          if (obj.id && obj.id > 0) updateList.push(obj);
          else insertList.push(obj);
          return;
        }

        const maxStr = r.max_marks ?? '';
        const passStr = r.passing_marks ?? '';

        const maxEntered = !isEmpty(maxStr);
        const passEntered = !isEmpty(passStr);

        if (!maxEntered && !passEntered) return;
        if (!maxEntered || !passEntered) {
          hasInvalid = true;
          return;
        }

        const max = toNum(maxStr);
        const pass = toNum(passStr);

        if (max <= 0 || pass < 0 || pass > max) {
          hasInvalid = true;
          return;
        }

        hasAnyValid = true;

        const obj = {
          id: isExisting ? Number(r.id) : null,
          vendor_id: selectionData.vendor_id
            ? Number(selectionData.vendor_id)
            : null,
          branch_id: selectionData.branch_id
            ? Number(selectionData.branch_id)
            : null,
          course_id: Number(courseData.course_id),
          pattern_id: pid,
          subject_id: Number(r.subject_id),
          sem_no: semNo,
          evaluated_by: EVALUATED_BY.MARKS,
          max_marks: max,
          passing_marks: pass,
          passing_grade: null,
        };

        if (obj.id && obj.id > 0) updateList.push(obj);
        else insertList.push(obj);
      });
    });

    if (hasInvalid) {
      toast.error('Invalid marks: Max must be > 0 and Passing must be ≤ Max');
      return;
    }

    if (!hasAnyValid) {
      toast.error('Please enter Marks/Grade for at least one subject');
      return;
    }

    try {
      if (insertList.length > 0) {
        const resIns = await apiService.post(
          'api/ExamMaster/AddCourseExamSubPattern',
          insertList,
        );
        if (resIns?.status !== true) {
          toast.error(resIns?.Message || resIns?.message || 'Insert failed');
          return;
        }
      }

      if (updateList.length > 0) {
        const resUpd = await apiService.put(
          'api/ExamMaster/UpdateCourseExamSubPattern',
          updateList,
        );
        if (resUpd?.status !== true) {
          toast.error(resUpd?.Message || resUpd?.message || 'Update failed');
          return;
        }
      }

      toast.success(isEditMode ? 'Updated successfully' : 'Saved successfully');
      navigate('/course-list');
    } catch (err) {
      console.error(err);
      toast.error('Something went wrong');
    }
  };

  // ----------------------------
  // Render pattern section
  // ----------------------------
  const renderPatternSection = (semNum, termKey) => {
    const p =
      patternBySemTerm[`${semNum}|${termKey}`] ||
      patternBySemTerm[`0|${termKey}`] ||
      null;

    const pid = p ? pickPatternId(p) : 0;
    const allRows = pid ? termSubjects[pid] || [] : [];

    const rows = allRows.filter(
      (r) => Number(r.semester_no || r.sem_no || 1) === semNum,
    );

    const title =
      (p && (pickTermName(p) || termKey)) ||
      (termKey ? String(termKey) : 'Pattern');

    const totalMarks = p ? pickTotalMarks(p) : '';
    const weightage = p ? pickWeightage(p) : '';

    const hasMarks = rows.some(
      (r) =>
        normalizeEval(r.evaluated_by || evaluatedByMode) === EVALUATED_BY.MARKS,
    );
    const hasGrade = rows.some(
      (r) =>
        normalizeEval(r.evaluated_by || evaluatedByMode) === EVALUATED_BY.GRADE,
    );

    const colCount = 4 + (hasMarks ? 2 : 0) + (hasGrade ? 1 : 0);

    return (
      <div className='mb-4' key={`${semNum}_${termKey}`}>
        <h5 className='text-primary mb-2'>
          {title}
          {hasMarks && totalMarks ? (
            <small className='text-muted'> (Total: {totalMarks})</small>
          ) : null}
          {hasMarks && weightage ? (
            <small className='text-muted'> (Weightage: {weightage}%)</small>
          ) : null}
        </h5>

        <table className='table table-bordered table-hover'>
          <thead className='bg-primary text-white'>
            <tr>
              <th style={{ width: 80 }}>S. no.</th>
              <th>Subject Name</th>
              <th style={{ width: 200 }}>Subject Code</th>
              <th style={{ width: 220 }}>Evaluated By</th>

              {hasMarks ? (
                <>
                  <th style={{ width: 180 }}>Max Marks</th>
                  <th style={{ width: 180 }}>Passing Marks</th>
                </>
              ) : null}

              {hasGrade ? <th style={{ width: 220 }}>Passing Grade</th> : null}
            </tr>
          </thead>

          <tbody>
            {p && rows.length > 0 ? (
              rows.map((r, i) => {
                const originalIndex = allRows.findIndex(
                  (x) => String(x.tempId) === String(r.tempId),
                );
                const idx = originalIndex >= 0 ? originalIndex : i;

                const rowMode = normalizeEval(
                  r.evaluated_by || evaluatedByMode,
                );
                const isGrade = rowMode === EVALUATED_BY.GRADE;

                return (
                  <tr key={r.tempId || i}>
                    <td>{i + 1}</td>

                    <td>
                      <input
                        type='text'
                        className='form-control'
                        value={r.subject_name || ''}
                        readOnly
                      />
                    </td>

                    <td>
                      <input
                        type='text'
                        className='form-control'
                        value={r.subject_code || ''}
                        readOnly
                      />
                    </td>

                    <td>
                      <Select
                        options={EVAL_OPTIONS}
                        value={EVAL_OPTIONS.find((x) => x.value === rowMode)}
                        onChange={(opt) =>
                          updateRowEvaluatedBy(pid, idx, opt?.value)
                        }
                        isSearchable={false}
                        isClearable={false}
                        menuPortalTarget={document.body}
                        styles={{
                          menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                          control: (base) => ({
                            ...base,
                            minHeight: 34,
                            height: 34,
                          }),
                          valueContainer: (base) => ({
                            ...base,
                            height: 34,
                            padding: '0 8px',
                          }),
                          indicatorsContainer: (base) => ({
                            ...base,
                            height: 34,
                          }),
                        }}
                      />
                    </td>

                    {hasMarks ? (
                      <>
                        <td>
                          {!isGrade ? (
                            <input
                              type='number'
                              className='form-control'
                              value={r.max_marks ?? ''}
                              placeholder='Max'
                              onChange={(e) =>
                                updateRowField(
                                  pid,
                                  idx,
                                  'max_marks',
                                  e.target.value,
                                )
                              }
                            />
                          ) : (
                            <input
                              type='text'
                              className='form-control'
                              value=''
                              readOnly
                            />
                          )}
                        </td>

                        <td>
                          {!isGrade ? (
                            <input
                              type='number'
                              className='form-control'
                              value={r.passing_marks ?? ''}
                              placeholder='Pass'
                              onChange={(e) =>
                                updateRowField(
                                  pid,
                                  idx,
                                  'passing_marks',
                                  e.target.value,
                                )
                              }
                            />
                          ) : (
                            <input
                              type='text'
                              className='form-control'
                              value=''
                              readOnly
                            />
                          )}
                        </td>
                      </>
                    ) : null}

                    {hasGrade ? (
                      <td>
                        {isGrade ? (
                          <Select
                            options={GRADE_SCALE}
                            value={GRADE_SCALE.find(
                              (g) =>
                                String(g.value) ===
                                String(r.passing_grade || ''),
                            )}
                            onChange={(opt) =>
                              updateRowField(
                                pid,
                                idx,
                                'passing_grade',
                                opt?.value || '',
                              )
                            }
                            isClearable
                            isSearchable={false}
                            placeholder='Select Grade'
                            menuPortalTarget={document.body}
                            styles={{
                              menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                              control: (base) => ({ ...base, minHeight: 34 }),
                            }}
                          />
                        ) : (
                          <input
                            type='text'
                            className='form-control'
                            value=''
                            readOnly
                          />
                        )}
                      </td>
                    ) : null}
                  </tr>
                );
              })
            ) : (
              <tr>
                <td colSpan={colCount} className='text-center'>
                  {p
                    ? `No subjects found for ${structureWordUpper(courseStructure)} ${semNum}`
                    : 'Pattern not found'}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    );
  };

  const showCourseDropdown = !(
    fromPatternList ||
    fixedCourseFromState ||
    isEditMode
  );

  return (
    <>
      <div className='bg-white pd-5 card-box mb-5 toolbar-row sticky-top border text-right mb-2'>
        <Button
          color='secondary'
          size='sm'
          onClick={() => navigate('/course-list')}
          className='mr-2'
        >
          <i className='bi bi-arrow-left' /> Back
        </Button>

        <Button color='primary' size='sm' onClick={Save}>
          <i className='bi bi-check-circle' /> {isEditMode ? 'Update' : 'Save'}
        </Button>
      </div>

      <VendorBranchSelector
        user={user}
        selectionData={selectionData}
        setSelectionData={setSelectionData}
        errorMessage={errorMessage}
        accessButton={true}
      />

      <Card className='card-box height-100-p pd-20 mb-20'>
        <CardTitle
          tag='h6'
          className='border-bottom p-3 mb-0 d-flex align-items-center justify-content-between'
        >
          <div>Course Exam Subject Pattern</div>
        </CardTitle>

        <CardBody>
          <Form className='row'>
            <FormGroup className='col-6'>
              <Label>Course</Label>

              {showCourseDropdown ? (
                <>
                  <Select
                    options={courseOptions}
                    value={courseOptions.find(
                      (o) => Number(o.value) === Number(courseData.course_id),
                    )}
                    onChange={onCourseSelectChange}
                    onInputChange={(inputValue, actionMeta) => {
                      if (actionMeta.action === 'input-change')
                        fetchCourses(inputValue);
                    }}
                    isSearchable
                    isClearable
                    placeholder='Search courses ...'
                  />
                  {!courseData.course_id ? (
                    <FormText color='muted'>
                      Select course to load Exam Terms + Subjects below
                    </FormText>
                  ) : null}
                </>
              ) : (
                <>
                  <input
                    type='text'
                    className='form-control'
                    value={
                      courseData.course_name ||
                      (courseData.course_id
                        ? `Course-${courseData.course_id}`
                        : '')
                    }
                    readOnly
                  />
                  <FormText color='muted'>
                    Course is auto-selected from navigation / existing data
                  </FormText>
                </>
              )}

              {courseData.course_id && totalSemester ? (
                <FormText color='muted'>
                  Total {structureWordUpper(courseStructure)}:{' '}
                  <b>{totalSemester}</b>
                </FormText>
              ) : null}
            </FormGroup>
          </Form>
        </CardBody>
      </Card>

      {courseData.course_id && totalSemester > 0 ? (
        <div className='faq-wrap'>
          <h4 className='mb-30 h4 text-blue padding-top-30'>
            {structureWordUpper(courseStructure)} Wise Exam Subject Pattern
          </h4>

          <div className='padding-bottom-30'>
            {Array.from({ length: totalSemester }, (_, i) => {
              const semNum = i + 1;

              return (
                <div className='card mb-3' key={`sem_${semNum}`}>
                  <div className='card-header' id={`headingSem${semNum}`}>
                    <button
                      className='btn btn-block w-100 d-flex justify-content-between align-items-center'
                      type='button'
                      data-toggle='collapse'
                      data-target={`#collapseSem${semNum}`}
                      aria-controls={`collapseSem${semNum}`}
                      style={{ fontWeight: 600 }}
                    >
                      <span>{structureHeader(courseStructure, semNum)}</span>
                      <small className='text-muted'>(Click to show/hide)</small>
                    </button>
                  </div>

                  <div
                    id={`collapseSem${semNum}`}
                    className='collapse show'
                    aria-labelledby={`headingSem${semNum}`}
                  >
                    <div className='card-body'>
                      {termTypes && termTypes.length > 0
                        ? termTypes.map((t) => renderPatternSection(semNum, t))
                        : (patternList || []).map((p) => {
                            const pid = pickPatternId(p);
                            const fakeKey = String(
                              pickTermType(p) ||
                                pid ||
                                pickTermName(p) ||
                                'PATTERN',
                            );
                            return renderPatternSection(semNum, fakeKey);
                          })}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ) : courseData.course_id ? (
        <div className='text-center text-muted'>
          Total {structureWordUpper(courseStructure)} not found / subjects not
          loaded.
        </div>
      ) : null}
    </>
  );
};

export default AddEditCourseExamSubPattern;
